/**
 * Recording an ACA requirement as met — build 6B.
 *
 * The green program's checklist is computed where the platform can tell
 * (twelve metered months, a target, a reduction), documented where the
 * library holds the paper, and entered for the rest — the verifier and date,
 * the Scope 3 map, the retired credits. Nothing could be entered until now.
 * The entry is kept in the green program's note with who and when; marking a
 * requirement met needs the evidence in words.
 */
import { Fragment, useState } from "react";
import { useSession } from "../../app/SessionContext";
import { Button, Field, Input, Segmented } from "../../design/components";
import { checklistRefusal, setChecklistItem } from "../../domain/executiveEntries";
import type { ChecklistEvidence, Requirement } from "../../domain/green";
import { t } from "../../i18n";
import s from "./EntryForms.module.css";

export function AcaRequirementRow({
  r,
  item,
  checklist,
  rowClass,
  ready = true,
  onSave,
  children,
}: {
  r: Requirement;
  item: ChecklistEvidence | undefined;
  checklist: Record<string, ChecklistEvidence> | undefined;
  rowClass?: string;
  /** false until the note is loaded: a save before then would write over it with the seed */
  ready?: boolean;
  onSave: (next: Record<string, ChecklistEvidence>) => Promise<void> | void;
  /** the row's cells as the screen already drew them */
  children: React.ReactNode;
}) {
  const session = useSession();
  const [open, setOpen] = useState(false);
  const [met, setMet] = useState<"met" | "not">(item?.met ? "met" : "not");
  const [evidence, setEvidence] = useState(item?.evidence ?? "");
  const [why, setWhy] = useState<string | null>(null);
  const enterable = r.evidence !== "computed";

  const save = async (clear = false) => {
    if (clear) {
      setOpen(false);
      await onSave(setChecklistItem(checklist, r.key, null));
      return;
    }
    const refused = checklistRefusal(met === "met", evidence);
    if (refused) return setWhy(refused);
    setWhy(null);
    setOpen(false);
    await onSave(
      setChecklistItem(checklist, r.key, {
        met: met === "met",
        evidence: evidence.trim() || undefined,
        by: session.principal.name,
        at: new Date().toISOString(),
      }),
    );
  };

  return (
    <Fragment>
      <tr data-testid={`aca-${r.key}`} className={rowClass}>
        {children}
        <td>
          {enterable && ready ? (
            <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)} data-testid={`aca-record-${r.key}`}>
              {item ? t("Change") : t("Record")}
            </Button>
          ) : enterable ? null : (
            <span className={s.muted}>{t("computed")}</span>
          )}
        </td>
      </tr>
      {open && (
        <tr className={s.formRow}>
          <td colSpan={4}>
            <div className={s.form} data-testid={`aca-form-${r.key}`}>
              <div>
                <Segmented
                  ariaLabel="Requirement status"
                  value={met}
                  onChange={setMet}
                  options={[
                    { value: "met", label: "Met" },
                    { value: "not", label: "Not met" },
                  ]}
                />
              </div>
              <div className={s.wide}>
                <Field label="Evidence — the verifier and date, or where it is held">
                  <Input value={evidence} onChange={(e) => setEvidence(e.target.value)} maxLength={500} />
                </Field>
              </div>
              {why && (
                <p className={`${s.err} ${s.wide}`} role="alert">
                  {t(why)}
                </p>
              )}
              <div className={`${s.actions} ${s.wide}`}>
                <Button variant="primary" onClick={() => void save()}>
                  {t("Save")}
                </Button>
                {item && (
                  <Button variant="ghost" onClick={() => void save(true)}>
                    {t("Clear the entry")}
                  </Button>
                )}
                <Button variant="ghost" onClick={() => setOpen(false)}>
                  {t("Cancel")}
                </Button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  );
}
